import { motion } from "framer-motion";
import { Microscope, Shield, Activity, FileText, UploadCloud, Brain } from "lucide-react";

const FEATURES = [
  {
    icon: UploadCloud,
    title: "Drag & Drop Detection",
    desc: "Upload dermoscopic or clinical images — JPG, PNG or HEIC — and receive classification within seconds.",
    tone: "sky",
  },
  {
    icon: Brain,
    title: "Multi-Agent Collaboration",
    desc: "Vision, Classifier, Validator and Report agents orchestrated through the AGNO framework.",
    tone: "indigo",
  },
  {
    icon: FileText,
    title: "Real-time Medical Reports",
    desc: "Structured clinical summaries with severity, differential reasoning and next-step recommendations.",
    tone: "emerald",
  },
  {
    icon: Activity,
    title: "High Accuracy Engine",
    desc: "Ensemble of CNN, DenseNet121 and SwinViT achieving ~88.7% validation accuracy on ISIC-2019.",
    tone: "slate",
  },
  {
    icon: Microscope,
    title: "Doctor Dashboard",
    desc: "JWT-secured dashboard with patient history, prediction archive and exportable reports.",
    tone: "sky",
  },
  {
    icon: Shield,
    title: "Secure by Design",
    desc: "httpOnly cookies, MongoDB at rest and in-memory image processing — nothing retained by default.",
    tone: "emerald",
  },
];

const toneMap = {
  sky: "from-sky-100 to-sky-50 text-sky-700 ring-sky-200/70",
  indigo: "from-indigo-100 to-indigo-50 text-indigo-700 ring-indigo-200/70",
  emerald: "from-emerald-100 to-emerald-50 text-emerald-700 ring-emerald-200/70",
  slate: "from-slate-100 to-slate-50 text-slate-700 ring-slate-200/70",
};

export default function Features() {
  return (
    <section id="features" className="py-24 md:py-32 bg-white">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="max-w-2xl">
          <div className="text-xs uppercase tracking-[0.22em] font-bold text-slate-500">
            <span className="font-mono-num">02</span> · Capabilities
          </div>
          <h2 className="font-heading mt-4 text-4xl sm:text-5xl tracking-tight font-light text-slate-900">
            Built for clinicians, <br />
            trusted by <em className="not-italic text-slate-400">the data</em>.
          </h2>
          <p className="mt-5 text-slate-600 max-w-xl">
            Six capabilities that turn a single skin image into a clear,
            defensible, clinician-ready decision.
          </p>
        </div>

        <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {FEATURES.map((f, i) => {
            const Icon = f.icon;
            return (
              <motion.div
                key={f.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.55, delay: i * 0.05 }}
                className="group rounded-2xl border border-slate-200 bg-white p-7 transition-all hover:-translate-y-1 hover:shadow-[0_16px_60px_-20px_rgba(15,23,42,0.25)] hover:border-slate-300"
                data-testid={`feature-card-${i}`}
              >
                <div
                  className={`inline-flex h-11 w-11 items-center justify-center rounded-xl bg-gradient-to-br ${toneMap[f.tone]} ring-1`}
                >
                  <Icon className="h-5 w-5" strokeWidth={2} />
                </div>
                <h3 className="font-heading mt-5 text-xl tracking-tight text-slate-900">
                  {f.title}
                </h3>
                <p className="mt-2 text-sm text-slate-600 leading-relaxed">{f.desc}</p>
                <div className="mt-6 flex items-center justify-between text-[10px] uppercase tracking-[0.2em] font-mono-num text-slate-400">
                  <span>Feature · 0{i + 1}</span>
                  <span className="h-px w-12 bg-slate-200 group-hover:bg-slate-400 transition-colors" />
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
